/**
 * AHP progress note draft fields and client-side quality gate (pure).
 */

export interface ProgressNoteDraftFields {
  visitedPlaces: string
  sessionPrep: string
  sessionSummary: string
  goalProgress: string
  smartGoalsComments: string
}

export const PROGRESS_NOTE_FIELD_KEYS = [
  'visitedPlaces',
  'sessionPrep',
  'sessionSummary',
  'goalProgress',
  'smartGoalsComments',
] as const satisfies readonly (keyof ProgressNoteDraftFields)[]

export const PROGRESS_NOTE_MIN_WORDS = 150
export const PROGRESS_NOTE_MAX_QA_ROUNDS = 3
/** Max voice recording length per post-session segment (seconds). */
export const PROGRESS_NOTE_SEGMENT_MAX_SECONDS = 120

export const POST_SESSION_RECORDING_FIELDS = [
  'visitedPlaces',
  'sessionSummary',
  'goalProgress',
] as const satisfies readonly (keyof ProgressNoteDraftFields)[]

export type PostSessionRecordingField = (typeof POST_SESSION_RECORDING_FIELDS)[number]

/** How long each coaching prompt stays visible during segment recording (ms). */
export const PROGRESS_NOTE_RECORDING_PROMPT_INTERVAL_MS = 3000

/** Rotating voice prompts shown during post-session segment recording. */
export const PROGRESS_NOTE_SEGMENT_RECORDING_PROMPTS: Record<
  PostSessionRecordingField,
  readonly string[]
> = {
  visitedPlaces: [
    'Where did you go?',
    'What places did you visit?',
    'What events did you attend?',
    'Were you just at home?',
  ],
  sessionSummary: [
    'What happened during the session?',
    'What support did you provide?',
    'What did the member say?',
    'Any new thoughts from the member?',
    'What was their mood like?',
    'Any key observations?',
  ],
  goalProgress: [
    'How did the member progress toward their goals?',
    'What steps did they take today?',
    'Any barriers to their goals?',
    'What wellbeing or goal outcomes did you notice?',
  ],
}

export function getProgressNoteRecordingPrompts(
  field: PostSessionRecordingField,
): readonly string[] {
  return PROGRESS_NOTE_SEGMENT_RECORDING_PROMPTS[field]
}

export type ProgressNoteQualityIssueCode =
  | 'missing_field'
  | 'summary_too_short'
  | 'visited_places_mismatch'

export interface ProgressNoteQualityIssue {
  code: ProgressNoteQualityIssueCode
  field?: keyof ProgressNoteDraftFields
  message: string
}

export interface ProgressNoteQualityResult {
  passed: boolean
  wordCount: number
  issues: ProgressNoteQualityIssue[]
  /** Human-readable gaps for the Q&A re-record prompt. */
  missingTopics: string[]
}

export function countWords(text: string): number {
  const trimmed = text.trim()
  if (!trimmed) return 0
  return trimmed.split(/\s+/).filter(Boolean).length
}

export function applySegmentTranscriptToDraft(
  field: keyof ProgressNoteDraftFields,
  transcript: string,
  draft: ProgressNoteDraftFields,
): ProgressNoteDraftFields {
  return { ...draft, [field]: transcript.trim() }
}

/** Dev fallback transcript when transcribe Lambda is unavailable. */
export function mockSegmentTranscript(
  field: (typeof POST_SESSION_RECORDING_FIELDS)[number],
): string {
  switch (field) {
    case 'visitedPlaces':
      return 'Member home, local shops on Federation Street, and community centre.'
    case 'sessionSummary':
      return (
        'We worked on community access and daily living skills. The member engaged well throughout ' +
        'the session with appropriate support. Activities were completed as planned with observations ' +
        'recorded for the care team. Next steps were discussed with the participant where relevant.'
      )
    case 'goalProgress':
      return 'Progress toward independence and community participation goals was observed during the session.'
  }
}

export function emptyProgressNoteDraft(): ProgressNoteDraftFields {
  return {
    visitedPlaces: '',
    sessionPrep: '',
    sessionSummary: '',
    goalProgress: '',
    smartGoalsComments: '',
  }
}

const FIELD_LABELS: Record<keyof ProgressNoteDraftFields, string> = {
  visitedPlaces: 'visited places',
  sessionPrep: 'session preparation and research',
  sessionSummary: 'session summary',
  goalProgress: 'goal progress',
  smartGoalsComments: 'SMART goals comments',
}

/** Normalize location strings for loose comparison (check-in vs visited places). */
export function normalizeLocationToken(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^\w\s]/g, ' ')
    .replace(/\s+/g, ' ')
}

/**
 * Warn when the check-in location does not appear in visited places text.
 * Non-blocking — surfaced as a quality issue, not a hard error.
 */
export function visitedPlacesMismatch(
  visitedPlaces: string,
  checkInLocationText: string | null | undefined,
): boolean {
  const location = checkInLocationText?.trim()
  if (!location || !visitedPlaces.trim()) return false
  const normalizedVisited = normalizeLocationToken(visitedPlaces)
  const normalizedCheckIn = normalizeLocationToken(location)
  if (!normalizedCheckIn) return false
  const checkTokens = normalizedCheckIn.split(' ').filter((t) => t.length > 2)
  if (checkTokens.length === 0) return false
  const matched = checkTokens.filter((token) => normalizedVisited.includes(token)).length
  return matched < Math.min(2, checkTokens.length)
}

export function validateProgressNoteDraft(
  fields: ProgressNoteDraftFields,
  opts?: { checkInLocationText?: string | null },
): ProgressNoteQualityResult {
  const issues: ProgressNoteQualityIssue[] = []
  const missingTopics: string[] = []

  for (const key of PROGRESS_NOTE_FIELD_KEYS) {
    if (!fields[key].trim()) {
      issues.push({
        code: 'missing_field',
        field: key,
        message: `Missing ${FIELD_LABELS[key]}.`,
      })
      missingTopics.push(FIELD_LABELS[key])
    }
  }

  const wordCount = countWords(fields.sessionSummary)
  if (wordCount < PROGRESS_NOTE_MIN_WORDS) {
    issues.push({
      code: 'summary_too_short',
      field: 'sessionSummary',
      message: `Session summary needs at least ${PROGRESS_NOTE_MIN_WORDS} words (currently ${wordCount}).`,
    })
    if (!missingTopics.includes(FIELD_LABELS.sessionSummary)) {
      missingTopics.push(`a longer session summary (${PROGRESS_NOTE_MIN_WORDS}+ words)`)
    }
  }

  if (visitedPlacesMismatch(fields.visitedPlaces, opts?.checkInLocationText)) {
    issues.push({
      code: 'visited_places_mismatch',
      field: 'visitedPlaces',
      message: 'Visited places may not match the session check-in location.',
    })
    missingTopics.push('confirmation of visited places vs check-in location')
  }

  return {
    passed: issues.length === 0,
    wordCount,
    issues,
    missingTopics,
  }
}

/** Prompt copy for the Q&A re-record loop. */
export function progressNoteQaPrompt(missingTopics: string[]): string {
  if (missingTopics.length === 0) return ''
  const listed = missingTopics.map((t) => t.trim()).filter(Boolean)
  if (listed.length === 1) {
    return `You mentioned some details, but we still need: ${listed[0]}.`
  }
  const head = listed.slice(0, -1).join(', ')
  const tail = listed[listed.length - 1]
  return `You mentioned some details, but we still need: ${head}, and ${tail}.`
}

/** Mock structured draft used in dev when Bruno / AHP AI is unavailable. */
export function mockProgressNoteDraftFromTranscript(transcript: string): ProgressNoteDraftFields {
  const snippet = transcript.trim().slice(0, 500)
  const filler =
    'The participant engaged well throughout the session. Activities were completed as planned with appropriate support and encouragement. Observations were recorded for the care team and next steps were discussed with the participant and/or their support network where relevant.'
  let summary = `${snippet}\n\n${filler}`.trim()
  while (countWords(summary) < PROGRESS_NOTE_MIN_WORDS) {
    summary += ` ${filler}`
  }
  return {
    visitedPlaces: 'Community access — session location and nearby areas as discussed.',
    sessionPrep: 'Reviewed prior notes and support plan before the session.',
    sessionSummary: summary,
    goalProgress: 'Progress toward agreed goals was observed; details captured in summary.',
    smartGoalsComments: 'SMART goal comments to be refined once AHP goal sync is live.',
  }
}
